import { CSSProperties, useCallback, useEffect, useRef } from 'react';
import { useMultiStepFormContext } from './MultiStepFormProvider';
import { StyledFormSectionWrapper } from './styles';

const FOCUSABLE_SELECTOR = ['textarea:not([disabled])', 'input:not([disabled])', 'select:not([disabled])'].join(',');

// This avoids focusing elements that exist in the DOM but aren’t actually visible.
function isVisible(el: HTMLElement) {
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
}

const FormContent = () => {
    const { stepsConfig, goForward, goNext, currentStep, handleSubmit } = useMultiStepFormContext<any>();

    // Stores DOM references to each step’s wrapper, indexed by step number.
    const stepRefs = useRef<Array<HTMLDivElement | null>>([]);
    const didAutoFocusRef = useRef<number | null>(null);

    const handleSectionClick = (targetIndex: number) => async () => {
        await goForward(targetIndex);
    };

    const getStepFocusables = useCallback((): HTMLElement[] => {
        const stepRoot = stepRefs.current[currentStep];
        if (!stepRoot) return [];
        return Array.from(stepRoot.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
    }, [currentStep]);

    const isLastFocusableInStep = useCallback(
        (target: HTMLElement) => {
            const focusables = getStepFocusables();
            if (focusables.length === 0) return false;
            return target === focusables[focusables.length - 1];
        },
        [getStepFocusables]
    );

    // Allow going forward using ENTER  (only if we're at the last focusable element)
    const onFormKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLFormElement>) => {
            if (e.defaultPrevented) return;

            // Don’t hijack shortcuts
            if (e.altKey || e.ctrlKey || e.metaKey) return;

            const target = e.target as HTMLElement | null;
            if (!target) return;

            const tag = target.tagName.toLowerCase();

            // ENTER: only advance if we're on the last focusable in the step
            if (e.key === 'Enter') {
                if (target.closest('[data-enter-no-advance="true"]')) return;
                if (tag === 'textarea') return;
                if (tag === 'button' || tag === 'a') return;

                // If not last, keep the native behavior
                if (!isLastFocusableInStep(target)) return;

                // Attempt to go forward after validation
                e.preventDefault();
                await goNext();
            }
        },
        [goNext, isLastFocusableInStep]
    );

    // Focus on the first form element of the current section
    useEffect(() => {
        const stepRoot = stepRefs.current[currentStep];
        if (!stepRoot) return;

        // If we already focused something specific for this step, don't override it
        if (didAutoFocusRef.current === currentStep) return;

        const focusables = Array.from(stepRoot.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);

        if (focusables.length === 0) return;

        requestAnimationFrame(() => {
            focusables[0].focus();
            didAutoFocusRef.current = currentStep;
        });
    }, [currentStep]);

    return (
        <form onSubmit={handleSubmit} onKeyDown={onFormKeyDown}>
            {stepsConfig.map((step, idx) => {
                const Component = step.component;
                const isDisabled = !!step.disabled;

                const style: CSSProperties = idx > currentStep ? { cursor: 'pointer' } : { cursor: 'default' };

                return (
                    <StyledFormSectionWrapper
                        key={step.key as string}
                        // Attach refs to each form section
                        ref={(el: HTMLDivElement | null) => {
                            stepRefs.current[idx] = el;
                        }}
                        onClick={isDisabled ? undefined : handleSectionClick(idx)}
                        style={style}
                    >
                        <Component />
                    </StyledFormSectionWrapper>
                );
            })}
        </form>
    );
};

export default FormContent;
